/**
 * Journey lifecycle product helpers (FRONTEND-ONLY).
 * Backend remains authority for transitions, CAS, and permissions.
 */

export const JOURNEY_LIFECYCLE_STATUSES = Object.freeze([
  'DRAFT',
  'READY',
  'TRAVELING',
  'COMPLETED',
  'CANCELLED',
  'ARCHIVED',
]);

export const LIFECYCLE_PRODUCT_LABELS = Object.freeze({
  DRAFT: '規劃中',
  READY: '準備出發',
  TRAVELING: '旅程進行中',
  COMPLETED: '旅程已完成',
  CANCELLED: '旅程已取消',
  ARCHIVED: '已封存',
});

export const LIFECYCLE_PRODUCT_HINTS = Object.freeze({
  DRAFT: '完成基本安排後，可以將旅程標記為準備完成。',
  READY: '行程已準備完成。出發時開始旅程。',
  TRAVELING: '旅程進行中，可持續調整安排並記錄日記。',
  COMPLETED: '你仍可以補寫日記、整理照片與分享旅程。',
  CANCELLED: '此旅程已取消，無法繼續一般行程操作。',
  ARCHIVED: '此旅程已封存，僅供回顧。',
});

export const LIFECYCLE_PRIMARY_ACTIONS = Object.freeze({
  DRAFT: 'READY',
  READY: 'START',
  TRAVELING: 'COMPLETE',
});

export const LIFECYCLE_ACTION_LABELS = Object.freeze({
  READY: '準備完成',
  START: '開始旅程',
  COMPLETE: '完成旅程',
  CANCEL: '取消旅程',
  ARCHIVE: '封存旅程',
});

export const LIFECYCLE_CONFIRM_COPY = Object.freeze({
  READY: {
    title: '確認準備完成？',
    body: '確認將這趟旅程標記為準備完成？之後即可建立決定與書寫日記。',
  },
  START: {
    title: '確認開始旅程？',
    body: '確認開始這趟旅程？開始後會進入旅程進行中狀態。',
  },
  COMPLETE: {
    title: '確認完成旅程？',
    body: '確認完成這趟旅程？完成後仍可補寫日記。',
  },
  CANCEL: {
    title: '確認取消旅程？',
    body: '取消後旅程會進入已取消狀態，一般規劃與出發流程將停止。此操作需由擁有者執行。',
  },
  ARCHIVE: {
    title: '確認封存旅程？',
    body: '封存後無法再變更旅程狀態（不可逆）。僅已完成或已取消的旅程可封存。',
  },
});

function isActiveOwner(journey) {
  return journey?.requesterRole === 'OWNER';
}

function isActiveCoordinatorOrOwner(journey) {
  return journey?.requesterRole === 'OWNER' || journey?.requesterRole === 'COORDINATOR';
}

export function lifecycleProductLabel(status) {
  const key = String(status || '');
  return LIFECYCLE_PRODUCT_LABELS[key] || (key ? key : '未知狀態');
}

export function lifecycleProductHint(status) {
  const key = String(status || '');
  return LIFECYCLE_PRODUCT_HINTS[key] || '目前狀態無法進行旅程狀態變更。';
}

export function isKnownLifecycleStatus(status) {
  return JOURNEY_LIFECYCLE_STATUSES.includes(String(status || ''));
}

/**
 * UX capability flags. Backend remains final authority.
 * READY/START: OWNER or COORDINATOR.
 * COMPLETE/CANCEL/ARCHIVE: OWNER only.
 */
export function lifecycleActionCapabilities(journey) {
  const status = String(journey?.lifecycleStatus || '');
  const known = isKnownLifecycleStatus(status);
  const coord = isActiveCoordinatorOrOwner(journey);
  const owner = isActiveOwner(journey);
  return {
    canMarkReady: known && status === 'DRAFT' && coord,
    canStart: known && status === 'READY' && coord,
    canComplete: known && status === 'TRAVELING' && owner,
    canCancel: known && (status === 'DRAFT' || status === 'READY' || status === 'TRAVELING') && owner,
    canArchive: known && (status === 'COMPLETED' || status === 'CANCELLED') && owner,
  };
}

export function primaryLifecycleAction(journey) {
  const caps = lifecycleActionCapabilities(journey);
  const status = String(journey?.lifecycleStatus || '');
  const next = LIFECYCLE_PRIMARY_ACTIONS[status];
  if (next === 'READY' && caps.canMarkReady) return 'READY';
  if (next === 'START' && caps.canStart) return 'START';
  if (next === 'COMPLETE' && caps.canComplete) return 'COMPLETE';
  return null;
}

export function secondaryLifecycleActions(journey) {
  const caps = lifecycleActionCapabilities(journey);
  const actions = [];
  if (caps.canCancel) actions.push('CANCEL');
  if (caps.canArchive) actions.push('ARCHIVE');
  return actions;
}

export function lifecycleActionLabel(action) {
  return LIFECYCLE_ACTION_LABELS[action] || String(action || '');
}

export function lifecycleConfirmCopy(action) {
  return LIFECYCLE_CONFIRM_COPY[action] || {
    title: '確認變更旅程狀態？',
    body: '請確認要繼續。',
  };
}

export function buildLifecycleMutationBody(expectedRowVersion, action, { reason } = {}) {
  const body = {
    expectedRowVersion,
    confirm: true,
  };
  if (action === 'CANCEL' && reason != null && String(reason).trim() !== '') {
    body.reason = String(reason).trim();
  }
  return body;
}

export function classifyLifecycleMutationError(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || cause?.message || '');
  if (code === 90003 || code === 90005 || code === 90006
      || key.includes('token.not.found') || key.includes('token.illegal') || key.includes('token.expired')) {
    return 'unauthorized';
  }
  if (key === 'travel.journey.concurrent.modification' || (code === 92003 && key.includes('concurrent'))) {
    return 'conflict';
  }
  if (key === 'travel.journey.invalid.lifecycle' || key.includes('invalid.lifecycle')) {
    return 'invalid-lifecycle';
  }
  if (code === 92002 || key === 'travel.journey.permission.denied' || key.includes('permission.denied')) {
    return 'forbidden';
  }
  if (code === 92001 || key === 'travel.journey.not.found' || key.includes('not.found')) {
    return 'not-found';
  }
  if (code === 92000 || key.includes('validation')) {
    return 'validation';
  }
  if (!cause?.raw && /network|timeout|failed/i.test(String(cause?.message || ''))) {
    return 'network';
  }
  return 'error';
}

export function lifecycleMutationErrorMessage(kind, detail) {
  if (kind === 'conflict') {
    return '這趟旅程剛剛有新的變更，已重新載入最新狀態。';
  }
  if (kind === 'invalid-lifecycle') {
    return detail || '目前狀態無法執行此操作，已重新載入最新狀態。';
  }
  if (kind === 'forbidden') {
    return '你沒有權限變更這趟旅程的狀態。';
  }
  if (kind === 'not-found') {
    return '找不到這趟旅程。';
  }
  if (kind === 'unauthorized') {
    return '登入狀態已失效，請重新登入。';
  }
  if (kind === 'validation') {
    return detail || '請確認後再試。';
  }
  if (kind === 'network') {
    return detail || '網路異常，請稍後再試。';
  }
  return detail || '無法更新旅程狀態，請稍後再試。';
}

export function normalizeLifecycleMutationResult(raw) {
  return {
    journeyId: raw?.journeyId == null ? null : String(raw.journeyId),
    lifecycleStatus: raw?.lifecycleStatus || '',
    rowVersion: raw?.rowVersion ?? null,
    readyAt: raw?.readyAt ?? null,
    startedAt: raw?.startedAt ?? null,
    cancelledAt: raw?.cancelledAt ?? null,
    completedAt: raw?.completedAt ?? null,
    archivedAt: raw?.archivedAt ?? null,
    noOp: Boolean(raw?.noOp),
  };
}

/**
 * TRAVEL-V2-I18N：呈現層翻譯用 key（既有中文字串函式保留給尚未遷移的頁面）。
 */
const LIFECYCLE_KEY = 'travel.journeyWorkspace.lifecycle';
const LIFECYCLE_ACTION_KEYS = new Set(['READY', 'START', 'COMPLETE', 'CANCEL', 'ARCHIVE']);

export function lifecycleStatusKey(status) {
  const key = String(status || '');
  return `${LIFECYCLE_KEY}.status.${JOURNEY_LIFECYCLE_STATUSES.includes(key) ? key : 'unknown'}`;
}

export function lifecycleHintKey(status) {
  const key = String(status || '');
  return `${LIFECYCLE_KEY}.hint.${JOURNEY_LIFECYCLE_STATUSES.includes(key) ? key : 'unknown'}`;
}

/** 動作按鈕、確認標題／內文／確認鈕的 key；未知動作回傳通用確認文案。 */
export function lifecycleActionKeys(action) {
  const key = LIFECYCLE_ACTION_KEYS.has(action) ? action : null;
  return {
    label: key ? `${LIFECYCLE_KEY}.action.${key}` : null,
    title: `${LIFECYCLE_KEY}.confirmTitle.${key || 'default'}`,
    body: `${LIFECYCLE_KEY}.confirmBody.${key || 'default'}`,
    confirm: `${LIFECYCLE_KEY}.confirmAction.${key || 'default'}`,
  };
}

/** 狀態變更失敗的文案 key（不含後端 detail；有 detail 時仍使用既有 lifecycleMutationErrorMessage）。 */
export function lifecycleMutationErrorKey(kind) {
  const known = ['conflict', 'invalid-lifecycle', 'forbidden', 'not-found', 'unauthorized', 'validation', 'network'];
  return `${LIFECYCLE_KEY}.error.${known.includes(kind) ? kind : 'generic'}`;
}
